// compare.jsx — side-by-side compare of any two texts (chavrusa view).
// Starts with the current piece and its live sources, then lets either side load
// any text returned by the complete library search.
const { useState: useStateC, useMemo: useMemoC, useRef: useRefC, useEffect: useEffectC } = React;

// Flatten everything comparable into one addressable list.
function buildComparables() {
  const out = [];
  const S = window.SICHA;
  if (S) out.push({
    id: "sicha", group: "This sicha", type: "Rebbe", title: S.titleEn, citation: S.volume.replace(/^.*· /, ""),
    he: S.segments.map((s) => s.he).join("\n\n"), en: S.segments.map((s) => s.en).join("\n\n"),
    relationship: "The current text",
    ref: S._liveRef || S.id,
    _full: true,
  });
  const seen = new Set(["center", "sicha"]);
  const addSource = (s, fallbackGroup = "Mareh mekomos") => {
    if (!s) return;
    const ref = s.srcRef || s.sourceRef || s.ref || s.citation || "";
    const genericTitle = !s.title || s.title === "Source";
    const id = s.id || ref || s.title;
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push({
      id,
      group: s.group || fallbackGroup,
      type: s.type || "Source",
      title: genericTitle ? (s.name || ref || "Source") : s.title,
      citation: s.citation || ref,
      he: s.he || "",
      en: s.en || "",
      relationship: s.relationship,
      n: s.n,
      ref,
    });
  };
  addSource(window.ACTIVE_COMPARE_SOURCE, "Selected source");
  // Live reader sources are always comparable.
  (window.SOURCES || []).forEach((s) => addSource(s));
  return out;
}

// a searchable picker dropdown for one pane
function ComparePicker({ items, value, onPick, side }) {
  const [open, setOpen] = useStateC(false);
  const [q, setQ] = useStateC("");
  const cur = items.find((i) => i.id === value);
  const groups = [];
  items.filter((i) => !q || `${i.title || ""} ${i.he || ""} ${i.en || ""} ${i.citation || ""}`.toLowerCase().includes(q.toLowerCase()))
    .forEach((i) => { let g = groups.find((x) => x.g === i.group); if (!g) { g = { g: i.group, items: [] }; groups.push(g); } g.items.push(i); });
  return (
    <div className="cmp-pick">
      <button className="cmp-pick-btn" onClick={() => setOpen((v) => !v)}>
        <span className="cmp-pick-cur">
          {cur ? <><TypeDot type={cur.type} size={8} /> <b>{cur.title}</b> <span>{cur.citation}</span></> : <span className="cmp-pick-ph">Choose a text…</span>}
        </span>
        <Icon d="chevd" size={15} style={{ opacity: .6, transform: open ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
      </button>
      {open && (
        <>
          <div className="cmp-pick-scrim" onClick={() => setOpen(false)} />
          <div className={"cmp-pick-menu cmp-pick-" + side}>
            <div className="cmp-pick-search"><Icon d="search" size={15} style={{ color: "var(--text-3)" }} />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter texts…" /></div>
            <div className="cmp-pick-list">
              {groups.map((grp) => (
                <div key={grp.g}>
                  <div className="cmp-pick-grp">{grp.g}</div>
                  {grp.items.map((i) => (
                    <button key={i.id} className={"cmp-pick-row" + (i.id === value ? " on" : "")}
                      onClick={() => { onPick(i.id); setOpen(false); setQ(""); }}>
                      <TypeDot type={i.type} size={8} />
                      <span className="cmp-pick-rowt"><b>{i.title}</b> <span>{i.citation}</span></span>
                      <span lang="he" dir="rtl" className="heb cmp-pick-he">{(i.he || i.en || "").slice(0, 16)}</span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ComparePane({ item, items, onPick, side, lang, onScroll, scrollRef }) {
  return (
    <section className="cmp-pane">
      <header className="cmp-pane-head">
        <ComparePicker items={items} value={item ? item.id : null} onPick={onPick} side={side} />
        {item && (
          <div className="cmp-pane-meta">
            <span className="chip"><TypeDot type={item.type} size={7} /> {item.type}</span>
            {item.relationship && <span className="cmp-rel">{item.relationship}</span>}
          </div>
        )}
      </header>
      <div className="cmp-pane-body" ref={scrollRef} onScroll={onScroll}>
        {!item ? (
          <div className="cmp-pane-empty"><Icon d="compare" size={24} style={{ color: "var(--text-3)" }} /><p>Pick a text to compare.</p></div>
        ) : (
          <>
            {lang !== "en" && item.he ? item.he.split("\n\n").map((p, i) => <p key={"h" + i} className="heb cmp-he">{p}</p>) : null}
            {lang !== "he" && item.en ? item.en.split("\n\n").map((p, i) => <p key={"e" + i} className="serif cmp-en">{p}</p>) : null}
            {!item.he && !item.en && <p className="cmp-pane-empty-text">Open the source in the reader to load its full text.</p>}
          </>
        )}
      </div>
    </section>
  );
}

function ComparePage({ pair, onChangePair, onBack, onOpenReader }) {
  const [liveTick, setLiveTick] = useStateC(0);
  useEffectC(() => {
    const bump = () => setLiveTick((n) => n + 1);
    window.addEventListener("mayanos-live-data", bump);
    window.addEventListener("mayanos-live-ready", bump);
    return () => {
      window.removeEventListener("mayanos-live-data", bump);
      window.removeEventListener("mayanos-live-ready", bump);
    };
  }, []);
  const [remoteItems, setRemoteItems] = useStateC([]);
  const [libraryQuery, setLibraryQuery] = useStateC("");
  const [libraryHits, setLibraryHits] = useStateC([]);
  const [searching, setSearching] = useStateC(false);
  const items = useMemoC(() => {
    const base = buildComparables();
    const ids = new Set(base.map((item) => item.id));
    return base.concat(remoteItems.filter((item) => !ids.has(item.id)));
  }, [liveTick, pair, remoteItems]);
  const byId = useMemoC(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);
  const isMobile = window.useIsMobile();
  const defaultA = byId.sicha ? "sicha" : (items[0] && items[0].id);
  const defaultB = (items.find((i) => i.id !== defaultA) || items[0] || {}).id;
  const a = (pair && byId[pair[0]] && pair[0]) || defaultA;
  const b = (pair && byId[pair[1]] && pair[1]) || defaultB;
  const [lang, setLang] = useStateC("both");
  const [sync, setSync] = useStateC(true);
  const refA = useRefC(null), refB = useRefC(null);
  const lock = useRefC(false);

  const setA = (id) => onChangePair([id, b]);
  const setB = (id) => onChangePair([a, id]);
  const swap = () => onChangePair([b, a]);

  useEffectC(() => {
    const query = libraryQuery.trim();
    if (query.length < 2) { setLibraryHits([]); setSearching(false); return; }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(() => fetch(`/bff/search?q=${encodeURIComponent(query)}&limit=20`)
      .then((response) => response.ok ? response.json() : [])
      .then((hits) => { if (!cancelled) setLibraryHits((hits || []).filter((hit) => hit.ref)); })
      .catch(() => { if (!cancelled) setLibraryHits([]); })
      .finally(() => { if (!cancelled) setSearching(false); }), 220);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [libraryQuery]);

  const loadForSide = async (hit, side) => {
    const ref = hit && (hit.ref || hit.citation);
    if (!ref) return;
    const response = await fetch(`/bff/text/${encodeURIComponent(String(ref).replace(/ /g, "_"))}?build=20260722-linking`);
    if (!response.ok) return;
    const text = await response.json();
    const item = {
      id: `library:${text.ref || ref}`, ref: text.ref || ref, group: "Library search", type: (text.categories || [])[0] || "Text",
      title: text.collection || text.title || hit.title || ref, citation: text.ref || ref,
      he: (text.segments || []).map((segment) => String(segment.he || "").replace(/<[^>]+>/g, " ")).join("\n\n"),
      en: (text.segments || []).map((segment) => String(segment.en || "").replace(/<[^>]+>/g, " ")).join("\n\n"),
      relationship: "Selected from the library", _full: true,
    };
    setRemoteItems((prior) => [item, ...prior.filter((existing) => existing.id !== item.id)]);
    onChangePair(side === "left" ? [item.id, b] : [a, item.id]);
  };

  useEffectC(() => {
    [[a, "left"], [b, "right"]].forEach(([id, side]) => {
      const item = byId[id];
      if (item && !item._full && item.ref && (!item.he || !item.en)) loadForSide(item, side);
    });
  }, [a, b]);

  const linkScroll = (from, to) => () => {
    if (!sync || isMobile || lock.current) return;
    const f = from.current, t = to.current; if (!f || !t) return;
    lock.current = true;
    const ratio = f.scrollTop / Math.max(1, f.scrollHeight - f.clientHeight);
    t.scrollTop = ratio * (t.scrollHeight - t.clientHeight);
    requestAnimationFrame(() => { lock.current = false; });
  };

  return (
    <div className="compare" data-screen-label="Compare">
      <div className="cmp-bar">
        <button className="mini-btn" onClick={onBack}><Icon d="back" size={15} /> Back</button>
        <div className="cmp-bar-title"><Icon d="compare" size={16} /> Compare</div>
        <div className="cmp-bar-r">
          <button className="mini-btn" onClick={swap} title="Swap sides"><Icon d="trans" size={15} /> {!isMobile && "Swap"}</button>
          {!isMobile && (
            <button className={"mini-btn" + (sync ? " on" : "")} onClick={() => setSync((v) => !v)} title="Synchronized scrolling">
              <Icon d="layers" size={15} /> Linked scroll
            </button>
          )}
          <div className="dirswitch" style={{ padding: 2 }}>
            {[["en", "EN"], ["both", "Both"], ["he", "עב"]].map(([k, l]) => (
              <button key={k} className={"dir-btn" + (lang === k ? " on" : "")} style={{ padding: "5px 10px" }} onClick={() => setLang(k)}>{l}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="cmp-presets">
        <span className="cmp-presets-l">Add any library text:</span>
        <div className="cmp-pick-search" style={{ flex: 1, maxWidth: 520 }}><Icon d="search" size={15} /><input value={libraryQuery} onChange={(event) => setLibraryQuery(event.target.value)} placeholder="Search by title, phrase, or reference…" /></div>
        {libraryQuery.trim().length >= 2 && <div className="cmp-pick-menu" style={{ left: 150, right: "auto", top: "100%", width: "min(680px, calc(100vw - 32px))", zIndex: 30 }}>
          <div className="cmp-pick-list">{searching ? <div className="cmd-empty">Searching the library…</div> : libraryHits.length ? libraryHits.map((hit) => <div key={hit.ref} className="cmp-pick-row"><span className="cmp-pick-rowt"><b>{hit.title || hit.ref}</b><span>{hit.ref}</span></span><button className="mini-btn" onClick={() => loadForSide(hit, "left")}>Left</button><button className="mini-btn" onClick={() => loadForSide(hit, "right")}>Right</button></div>) : <div className="cmd-empty">No matching texts.</div>}</div>
        </div>}
      </div>

      <div className={"cmp-grid" + (isMobile ? " cmp-grid-stack" : "")}>
        <ComparePane item={byId[a]} items={items} onPick={setA} side="left" lang={lang}
          scrollRef={refA} onScroll={linkScroll(refA, refB)} />
        <div className="cmp-divider"><span>vs</span></div>
        <ComparePane item={byId[b]} items={items} onPick={setB} side="right" lang={lang}
          scrollRef={refB} onScroll={linkScroll(refB, refA)} />
      </div>
    </div>
  );
}

Object.assign(window, { ComparePage });
